import { t } from "i18next";
import {
  dateFormatterDay,
  dateFormatterDayMonthLong,
  dateFormatterDayMonthYear,
} from "./format";
import { endOfMonth, startOfMonth } from "date-fns";

// Externally-visible signature
export function unreachable(p: never): never;
// Implementation signature
export function unreachable() {
  throw new Error("Switch is not exhaustive");
}

export const filteredValuesToTranslate = <T extends object>(
  values: T[],
  text: string,
  keysToTranslate?: Array<keyof T | string>,
  findKeysToTranslate?: (key: keyof T, value: string) => string,
  keysToIgnore?: Array<keyof T | string>,
) =>
  values.filter((row: T) => {
    const allKeys = Object.keys(row) as Array<keyof T>;
    const translatedValues = allKeys.reduce((newRow, key) => {
      if (keysToIgnore && keysToIgnore.includes(key)) {
        return newRow;
      } else if (
        keysToTranslate &&
        findKeysToTranslate &&
        keysToTranslate.includes(key)
      ) {
        return {
          ...newRow,
          [key]: t(findKeysToTranslate(key, row[key] as string))
            ? t(findKeysToTranslate(key, row[key] as string))
            : findKeysToTranslate(key, row[key] as string),
        };
      } else {
        return { ...newRow, [key]: row[key] };
      }
    }, {});
    const allValues = Object.values(translatedValues);
    const allValuesString = allValues.toString().toLowerCase().split(",");
    return text
      .split(" ")
      .every((word) =>
        allValuesString.find((values) => values.includes(word.toLowerCase())),
      );
  });

export const getDeliveryDate = (
  firstDeliveryDay: number,
  endDeliveryDay: number,
) => {
  const now = new Date();
  const firstDate = new Date(
    new Date().setDate(now.getDate() + firstDeliveryDay),
  );
  const lastDate = new Date(new Date().setDate(now.getDate() + endDeliveryDay));

  if (firstDate.getDay() === 5) {
    firstDate.setDate(firstDate.getDate() + 2);
    lastDate.setDate(lastDate.getDate() + 2);
  } else if (firstDate.getDay() === 6) {
    firstDate.setDate(firstDate.getDate() + 1);
    lastDate.setDate(lastDate.getDate() + 1);
  }

  if (lastDate.getDay() === 5) {
    lastDate.setDate(lastDate.getDate() + 2);
  } else if (lastDate.getDay() === 6) {
    lastDate.setDate(lastDate.getDate() + 1);
  }

  const firstDateDisplayed =
    firstDate.getMonth() === lastDate.getMonth()
      ? dateFormatterDay(firstDate)
      : dateFormatterDayMonthLong(firstDate);

  return firstDateDisplayed + "-" + dateFormatterDayMonthYear(lastDate);
};

export const adjustYaxisDomain = (minValue: number, maxValue: number) => {
  let adjustedMin: number;
  let adjustedMax: number;

  if (maxValue < 10) {
    // with decimals
    adjustedMin = Math.round((minValue - minValue * 0.1) * 100) / 100;
    adjustedMax = Math.round((maxValue + maxValue * 0.1) * 100) / 100;
  } else {
    // without decimals
    adjustedMin = Math.floor(minValue - minValue * 0.1);
    adjustedMax = Math.ceil(maxValue + maxValue * 0.1);
  }
  return [adjustedMin, adjustedMax];
};

export const getPreviousAndNextIds = <T extends { id: string }>(
  list: T[],
  currentId: string | null,
): { previousId: string | null; nextId: string | null } => {
  const index = currentId
    ? list.findIndex((item) => item.id === currentId)
    : -1;

  if (index === -1) {
    return { previousId: null, nextId: null };
  }

  return {
    previousId: index === 0 ? list[list.length - 1].id : list[index - 1].id,
    nextId: index === list.length - 1 ? list[0].id : list[index + 1].id,
  };
};

export const getMonthPeriodFromUrlParam = (
  monthParam: string | null,
): [start: string, end: string] | undefined => {
  if (!monthParam) return undefined;

  const yearMonth = monthParam.match(/^(\d{4})-(\d{2})$/);
  if (!yearMonth) return undefined;

  const year = Number(yearMonth[1]);
  if (year < 1000) return undefined;

  const month = Number(yearMonth[2]);
  if (month < 1 || month > 12) return undefined;

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  if (year > currentYear || (year === currentYear && month > currentMonth)) {
    return undefined;
  }

  const date = new Date(year, month - 1, 1);
  const start = startOfMonth(date);
  const end = endOfMonth(date);
  start.setHours(12, 0, 0, 0);
  end.setHours(12, 0, 0, 0);

  return [start.toISOString(), end.toISOString()];
};
